import { DateTime } from 'luxon';
import { useCallback } from 'react';

import { dateSelectors } from 'app-components/flux/date';
import { preferencesV2Actions, preferencesV2Selectors, preferencesV2Types } from 'app-components/flux/preferences-v2';
import { transactionsActions, transactionsSelectors } from 'app-components/flux/transactions';
import { tracker } from 'app-components/utils/core';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

/** Either a concrete year (e.g. 2024) or the "All time" sentinel. */
export type OnAfterYear = number | 'All';

export type RegisterOnAfterDateResult = {
  yearOnAfter: number | null;
  minDateYear: number | null;
  minDateISO: string | undefined;
  isAllTime: boolean;
  isLoading: boolean;
  diff: number;
  years: number[];
  value: OnAfterYear | null;
  selectYear: (year: OnAfterYear) => void;
};

type Params = {
  /** Read/write the in-memory selection instead of the saved preference. */
  temporarySelection?: boolean;
  /** Mixpanel prop for who initiated the change. */
  initiator?: string;
  /** When true (default), a selection dispatches the preference update + refetch. */
  controlled?: boolean;
  /** Called with the resolved ISO date (or null) on every selection. */
  onChange?: (dateIso: string | null) => void;
};

/**
 * Shared "transactions on or after {year}" logic — the year list, the current selection, and the
 * dispatch that applies it. Extracted from `RegisterOnAfterDate` so both the Select and the toolbar's
 * Since-dropdown stay in lockstep (notably the `minus({ days: 1 })` QCS boundary detail).
 */
export function useRegisterOnAfterDate({
  temporarySelection,
  initiator,
  controlled = true,
  onChange,
}: Params = {}): RegisterOnAfterDateResult {
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(transactionsSelectors.getIsLoading);
  const dateOnAfterISO: string | undefined = useAppSelector(
    temporarySelection
      ? transactionsSelectors.getDateOnAfter
      : (state) => preferencesV2Selectors.getPreferenceValue(state, 'dateOnAfter'),
  );
  const minDateISO = useAppSelector(transactionsSelectors.getEarliestDateOn);
  const today = useAppSelector(dateSelectors.getCurrentDate);

  const minDateYear = minDateISO ? DateTime.fromISO(minDateISO).year : null;
  // plus one because we minus one when sent to QCS
  const dateOnAfter = dateOnAfterISO ? DateTime.fromISO(dateOnAfterISO).plus({ days: 1 }) : null;
  const yearOnAfter = dateOnAfter?.isValid ? dateOnAfter.year : null;
  const diff = minDateYear ? today.year - minDateYear : 0;
  const isAllTime = !minDateISO || Boolean(dateOnAfterISO && dateOnAfterISO < minDateISO);
  const value: OnAfterYear | null = isAllTime ? 'All' : yearOnAfter;

  /** The descending year list shown above "All time" (e.g. [2026, 2025, ... minDateYear]). */
  const years = Array.from({ length: diff }, (_v, index) => today.year - index);

  const dateIsoForYear = useCallback(
    (year: OnAfterYear): string | null => {
      if (!minDateISO) return null;
      const date = year === 'All' ? DateTime.fromISO(minDateISO) : DateTime.local(Number(year), 1, 1);
      return date.minus({ days: 1 }).toISODate();
    },
    [minDateISO],
  );

  const selectYear = useCallback(
    (year: OnAfterYear) => {
      const dateIso = dateIsoForYear(year);
      if (dateIso) {
        onChange?.(dateIso);
        if (controlled) {
          tracker.track(tracker.events.transactionsOnDemand, {
            selectionType: temporarySelection ? 'temporary' : 'preference',
            date: dateIso,
            earliestDate: minDateISO,
            initiator,
          });
          if (!temporarySelection) {
            dispatch(
              preferencesV2Actions.updatePreferenceOverride(
                preferencesV2Types.mkPreferenceV2({ id: 'dateOnAfter', value: dateIso }),
              ),
            );
          }
          dispatch(transactionsActions.getTransactions(undefined, { props: { dateOnAfter: dateIso } }));
        }
      } else {
        onChange?.(null);
      }
    },
    [dateIsoForYear, onChange, controlled, temporarySelection, minDateISO, initiator, dispatch],
  );

  return { yearOnAfter, minDateYear, minDateISO, isAllTime, isLoading, diff, years, value, selectYear };
}
